import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { NextImage } from 'src/components/shared/elements/NextImage';
import { CartButton } from 'src/components/shared/elements/buttons/CartButton';
import { FavoriteButton } from 'src/components/shared/elements/buttons/FavoriteButton';
import { NoticeButton } from 'src/components/shared/elements/buttons/NoticeButton';
import { PriceWithLocalCurrency } from 'src/components/shared/elements/text/PriceWithLocalCurrency';
import { BuyerCommentLabel } from 'src/components/shared/products/BuyerCommentLabel';
import { ProductLabel } from 'src/components/shared/products/ProductLabel';
import { RatingStars } from 'src/components/shared/products/RatingStars';
import { KaimonoWebRoutes } from 'src/config/routes';
import { TOP_PAGE_PATH } from 'src/constants/pagePath';
import type { Product } from 'src/models/product/schema';
import { BrandsContext } from 'src/stores/Brands';
import { CategoryContext } from 'src/stores/Category';
import { I18nContext } from 'src/stores/I18n';
import type { LOCALE_TYPE } from 'src/types/locale';
import { brandName } from 'src/utils/brandName';
import { comma } from 'src/utils/comma';
import { isLocaleEn } from 'src/utils/locale';
import { getDiscountRateByLocale } from 'src/utils/medicine';
import { isAlcoholProduct, isDrugProduct } from 'src/utils/productHelper';
import styles from './index.module.scss';

type Props = {
  product: Product;
  section: string;
  imageSizeImmutable?: boolean;
  onClick?: () => void;
  isFavoriteHidden?: boolean;
  isCartAndNoticeHidden?: boolean;
  targetAttributionToken?: string;
};

export const ProductCard: React.FC<Props> = ({
  product,
  section,
  imageSizeImmutable = false,
  onClick,
  isFavoriteHidden,
  isCartAndNoticeHidden,
  targetAttributionToken
}) => {
  const { locale } = useRouter();
  const category = useContext(CategoryContext);
  const brands = useContext(BrandsContext);
  const localeResources = useContext(I18nContext);
  const { asPath } = useRouter();
  const isEn = isLocaleEn(locale as LOCALE_TYPE);
  const isTopPage = asPath === TOP_PAGE_PATH;

  return (
    <Link href={KaimonoWebRoutes.products.show(product.productId)}>
      <a aria-label={product.name} onClick={onClick} data-testid="product-item">
        <div className={styles.container}>
          <div className={styles.imageWrapper}>
            {!isTopPage && !isFavoriteHidden && <FavoriteButton product={product} section={section} />}
            <div className={styles.image}>
              <NextImage
                src={product.images[0]}
                width={300}
                height={300}
                layout="responsive"
                objectFit="contain"
                alt={product.name}
              />
              {product.hasBuyerComment && <BuyerCommentLabel sizeImmutable={imageSizeImmutable} />}
            </div>
          </div>
          <div className={styles.content}>
            {(isDrugProduct(product, category) || isAlcoholProduct(product, category)) && (
              <ProductLabel
                text={localeResources.products.medicine.couponText(getDiscountRateByLocale(locale))}
                isCategoryDrug={isDrugProduct(product, category)}
              />
            )}
            <div>
              <div className={styles.nameWrap}>
                <span className={styles.brand}>{brandName(product, brands)}</span>
                <span className={styles.name}>{product.name}</span>
              </div>
              {!isDrugProduct(product, category) && (
                <div className={styles.review}>
                  <RatingStars rating={product.reviewRatingAverage} count={product.reviewCount} withRating />
                </div>
              )}
            </div>
            <div className={styles.priceContainer}>
              <div className={styles.price}>
                <PriceWithLocalCurrency currency={product.currencySymbol} price={product.exchangeRateAdjustedPrice} />
                <div className={styles.local}>
                  JPY
                  {comma(product.undiscountedPrice)}
                </div>
              </div>
              {/* {!isTopPage && */}
              {!isCartAndNoticeHidden &&
                (product.orderable ? (
                  <CartButton product={product} section={section} targetAttributionToken={targetAttributionToken} />
                ) : (
                  <NoticeButton product={product} text={localeResources.products.stockNotification} />
                ))}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};
